"use client";

import { DEFAULT_SKILL_LUCIDE_ICON } from "@repo/utils";
import { useMemo } from "react";
import { cn } from "../../lib";
import type { SkillBadgeVariant } from "./octagon-badge-layer-tokens";
import {
	resolveSkillBadgeLucideMaskImage,
	resolveSkillBadgeLucideOutlineMaskImage,
} from "./skill-lucide-icon-mask";

export interface SkillLucideIconProps {
	icon?: string | null;
	size?: number;
	className?: string;
	variant?: SkillBadgeVariant;
}

/**
 * Amber gradient Lucide glyph via CSS mask (not per-path SVG gradient stroke).
 * Dark vector outline layer sits behind the gold fill for game-reward legibility.
 */
export function SkillLucideIcon({
	icon,
	size = 20,
	className,
	variant = "earned",
}: SkillLucideIconProps) {
	const resolvedIcon = icon ?? DEFAULT_SKILL_LUCIDE_ICON;
	const maskImage = useMemo(
		() => resolveSkillBadgeLucideMaskImage(resolvedIcon),
		[resolvedIcon],
	);
	const outlineMaskImage = useMemo(
		() => resolveSkillBadgeLucideOutlineMaskImage(resolvedIcon),
		[resolvedIcon],
	);

	return (
		<span
			className={cn(
				"sq-skill-badge-lucide-icon-stack relative inline-block shrink-0",
				variant === "placeholder" &&
					"sq-skill-badge-lucide-icon-stack--placeholder",
				className,
			)}
			style={{ width: size, height: size }}
			aria-hidden
		>
			<span
				className="sq-skill-badge-lucide-icon-outline absolute inset-0"
				style={{
					WebkitMaskImage: outlineMaskImage,
					maskImage: outlineMaskImage,
				}}
			/>
			<span
				className="sq-skill-badge-icon-gradient sq-skill-badge-lucide-icon absolute inset-0"
				style={{
					WebkitMaskImage: maskImage,
					maskImage,
				}}
			/>
		</span>
	);
}
